import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useRef } from 'preact/hooks';
import clsx from 'clsx';
import { getFormattedUnixTimestamp } from '##/utils/DateTimeHelpers';
import { getPipeSeperator } from '##/utils';
import { useObserver } from '##/utils/hooks';
import LazyImage from '../LazyImage';
import { LiveCarouselItem } from './types';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .carousel-item {
      display: flex;
      flex-direction: row;
      margin-bottom: pxToVw(6);
      margin-left: pxToVw(34);
    }

    .carousel-logo-container{ 
      height: pxToVw(106);
      width: pxToVw(131);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border-width: pxToVw(2);
      border-style: solid;
      border-color: transparent;
      background: #151515 0% 0% no-repeat padding-box;
      :global(.current-focus) & {
        border-radius: pxToVw(4);
        border-color: var(--borderColor);               
      }

    }

    .carousel-card-container {
      position: relative;
      width: pxToVw(405);
      height: pxToVw(106);
      margin-left: pxToVw(2);
      padding: pxToVw(20) pxToVw(12) pxToVw(20);
      color: $pure-white;
      font-family: barlow;
      border-width: pxToVw(2);
      border-style: solid;
      border-color: transparent;
      background: transparent linear-gradient(270deg, #121212 0%, #202020 100%) 0% 0% no-repeat padding-box;
      :global(.current-focus) & {
        border-radius: pxToVw(4);
        border-color: var(--borderColor);                    
        a {
          color: $pure-black;
        }
      }
    }

    .focused-card-container {
      padding: pxToVw(14) pxToVw(12) pxToVw(13);
    }

    .icon {
      object-fit: contain;
      padding: pxToVw(7);
      height: pxToVh(65);
      width: auto;
      max-width: pxToVw(115);
    }

    .icon-with-sponsor {
      @extend .icon;

      padding: 0 pxToVw(7);
      height: pxToVh(56);
      margin-bottom: pxToVh(5);
    }

    .sponsor-container {
      height: pxToVh(18);
      display: flex;
      align-items: center;

      & span {
        white-space: nowrap;
        font-size: pxToVw(8);
        margin-right: pxToVw(3);
      }

      & img {
        object-fit: contain;
        height: pxToVh(29);
        max-width: pxToVw(116);
        width: auto;
      }
    }

    .title {
      text-transform: capitalize;
      font-size: pxToVw(16);
      text-overflow: ellipsis;
      line-height: pxToVw(15);
      white-space: nowrap;
      width: pxToVw(235);
      overflow: hidden;
      margin-top: pxToVw(6);
      margin-bottom: pxToVw(6);
    }

    .time {
      font-size: pxToVw(13);
      color: $secondary-grey;
      margin: 0;
      margin-bottom: pxToVw(10);
      height: unset;
    }

    .poster {
      position: absolute;
      top: pxToVw(19);
      right: pxToVw(12);
      object-fit: cover;
      width : pxToVw(120);
      height : pxToVw(68);
    }

    .progress-bar-back {
      width: pxToVw(251);
      height: pxToVw(3);
      background-color: rgba(233, 233, 233, 0.3);
    }

    .progress-bar {
      height: pxToVw(3);
    }

    .focused-label {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: -(pxToVw(12));
      width: pxToVw(62);
      height: pxToVh(17);
      background: var(--borderColor);
    }

    .label-text {
      font-size: pxToVw(12);
      font-weight: 800;
      height: pxToVh(14);
    }
  
`;

const AdjustedLiveCarouselTile: FunctionalComponent<LiveCarouselItem & {
  focused: boolean;
  broughtToYouByLogo: string;
  color: string;
  logoUrl: string;
}> = ({
  liveShow,
  liveProgress,
  color,
  logoUrl,
  focused,
  broughtToYouByLogo,
}) => {
  const currentTime = liveProgress || 0;
  const { startTime = 0, endTime = 0 } = liveShow || {};
  const progressTime = Math.min(
    (currentTime / 1000 - startTime) / (endTime - startTime),
    1,
  );
  const border = color;
  const focusStyle = {
    '--borderColor': border,
  };

  const { rating, ratings } = liveShow || {};

  const liveRowRef = useRef<HTMLDivElement>(null);

  const isShowing = useObserver(liveRowRef);

  return (
    <div className={styles.carouselItem} ref={liveRowRef}>
      <div className={styles.carouselLogoContainer} style={focusStyle}>
        <LazyImage
          isShowing={isShowing}
          className={broughtToYouByLogo ? styles.iconWithSponsor : styles.icon}
          url={`${logoUrl}?image-profile=foxtel_livetv_channel_logo`}
        />
        {broughtToYouByLogo ? (
          <div className={styles.sponsorContainer}>
            <LazyImage isShowing={isShowing} url={`${broughtToYouByLogo}`} />
          </div>
        ) : null}
      </div>
      <div
        className={clsx(
          styles.carouselCardContainer,
          focused && styles.focusedCardContainer,
        )}
        style={focusStyle}
      >
        {focused ? (
          <div className={styles.focusedLabel} style={focusStyle}>
            <span className={styles.labelText}>on now</span>
          </div>
        ) : null}

        <p className={styles.title}>{liveShow?.title}</p>
        <p className={styles.time}>
          {getFormattedUnixTimestamp(startTime)}
          {' - '}
          {getFormattedUnixTimestamp(endTime)}
          {Boolean(rating || ratings) && (
            <>
              {getPipeSeperator(4)}
              {rating || ratings}
            </>
          )}
        </p>
        <div className={styles.progressBarBack}>
          <div
            className={styles.progressBar}
            style={{
              backgroundColor: color,
              width: `calc(100% * ${progressTime})`,
            }}
          />
        </div>
        <LazyImage
          isShowing={isShowing}
          className={styles.poster}
          url={`${liveShow?.landscapeImageUrl}?image-profile=foxtel_live_now_card`}
        />
      </div>
    </div>
  );
};

export default AdjustedLiveCarouselTile;
